import React, { FC, useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import ListGroup from 'react-bootstrap/ListGroup'
import { Link, useHistory } from 'react-router-dom'
import { useFormik } from 'formik'
import useMemberAccounts from '../../hooks/admin/useMemberAccounts'
import isAuthenticated from '../../routes/PrivateRoute'
import { numberWithCommas } from '../../utils/helpers'
import useMembers from '../../hooks/admin/useMembers'
import useEnvironment from '../../hooks/location/useEnviroment'
import useFindMember from '../../hooks/admin/useFindMember'
import { useGlobalState } from '../../hooks/store/useGlobalState'
import { providerResources } from '../../utils/constants'
import packageJson from '../../../package.json'
import PageTitle from '../../components/PageTitle'
import FhirLoader from '../../components/FhirLoader'
import useAlert from '../../hooks/authentication/useAlert'
import { Alert } from 'react-bootstrap'
import { useAuthState } from '../../hooks/store/useAuthState'
import { appConfig } from '../../assets/customizations/config'

const Admin: FC = () => {

  const {
    setFhirId,
    setProviderResource,
    setProviderSearchTerms
  } = useGlobalState((state) => ({
    setFhirId: state.setFhirId,
    setProviderResource: state.setProviderResource,
    setProviderSearchTerms: state.setProviderSearchTerms
  }))

  const [viewAllProviderFhirResource, setViewAllProviderFhirResource] = useState('')
  const [searchProviderResource, setSearchProviderResource] = useState({
    resource: '',
    searchTerms: ''
  })


  const history = useHistory()
  const { data: members, isLoading: countsLoading } = useMemberAccounts()

  const { isLoading, data: memberdata, refetch: getMembers, isRefetching, remove: removeMembers } = useMembers({
    category: 'All'
  })

  const { data: environment } = useEnvironment()
  useEffect(() => {
    if (environment) {
      reset()
    }
  }, [environment])

  const reset = () => {
    if (environment?.includeTotalInFHIRRequest) {
      getMembers()
    }

    remove()
    setViewAllProviderFhirResource('')
    setProviderSearchTerms('')
  }

  const memberDataForm = useFormik({
    initialValues: {
      memberId: ''
    },
    onSubmit: () => {
      getPatient()
    },
    enableReinitialize: true
  })
  const {
    alert: loginAlert,
    handleCloseAlert: handleCloseLoginAlert,
    showAlert: showLoginAlert
  } = useAlert({ show: false, variant: 'success', message: '' })
  const { loginSuccess, setLoginSuccess } = useAuthState()
  useEffect(() => {
    if (loginSuccess) {
      showLoginAlert('success', 'Successfully logged in')
      setLoginSuccess(false)
    }
  }, [loginSuccess])

  const { refetch: getPatient, isLoading: patientLoading, status: patientStatus, data: patient, remove } = useFindMember(memberDataForm.values.memberId, 'Member')

  useEffect(() => {
    if (patientStatus === 'success') {
      setFhirId(patient.id)
      history.push('/patient')
      remove()
    }
  }, [patientStatus])

  const handleSearchProviderView = (e: any, providerView: string) => {
    e.preventDefault()

    const resource = providerView === 'all' ? viewAllProviderFhirResource : searchProviderResource.resource
    setProviderResource(resource)

    if (providerView === 'search') {
      setProviderSearchTerms(searchProviderResource.searchTerms)
    }

    if (resource) {
      history.push(`/provider-list/${resource}`)
    }
  }
  const currentVersion = packageJson.version

  return (
    <section>
      <Container>
        <PageTitle displayName="Admin Home" />
        <Row>
          <Col md={3} className='p-3 border ms-5'>
            <h2 className='h3'>Admin Menu</h2>
            <ListGroup as='ul' variant='flush' className='m-0 p-0'>
              <ListGroup.Item as='li' className='mb-1 p-1'>
                <Link className='fw-bold' to='/members' onClick={() => removeMembers()}>Find Members</Link>
                {environment?.includeTotalInFHIRRequest && (
                  <span className='d-md-block'>
                    {(isLoading || isRefetching)
                      ? <FhirLoader message='Loading...' hidden={true} small={true} />
                      : memberdata && `(${numberWithCommas(memberdata?.total)} Members in Roster)`
                    }
                  </span>
                )}
              </ListGroup.Item>
              <ListGroup.Item as='li' className='mb-1 p-1'>
                <Link className='fw-bold' to='/accounts'>Member Accounts</Link>
                <span className='d-md-block'>
                  {countsLoading
                    ? <FhirLoader message='Loading...' hidden={true} small={true} />
                    : `(${members && numberWithCommas(members.memberCount)} Member Accounts)`
                  }
                </span>
              </ListGroup.Item>
              <ListGroup.Item as='li' className='mb-1 p-1'>
                {appConfig.developer_link ? (
                  <a className='fw-bold' href={appConfig.developer_link} target="_blank" rel="noopener noreferrer">Developer Content</a>
                ) : (
                  <Link className='fw-bold' to='/developers'>Developer Content</Link>
                )}
              </ListGroup.Item>
              <ListGroup.Item as='li' className='mb-1 p-1'>
                <div className='mb-2'>
                  <a className='fw-bold' href='/help/downloads/MemberPortalSupportGuide.pdf' target="_blank" rel="noopener noreferrer">Portal Support Guide</a>
                </div>
                <div>
                  <a className='fw-bold' href='/help/downloads/MemberPortalAdminGuide.pdf' target="_blank" rel="noopener noreferrer">Portal Admin Guide</a>
                </div>
              </ListGroup.Item>
              <ListGroup.Item as='li' className='mb-1 p-1'>
                <Link className='fw-bold' to='/release-notes'>
                  ConnectID Member Portal Release Notes
                </Link>
                <div className='small'>Current Version: {currentVersion}</div>
              </ListGroup.Item>

            </ListGroup>
          </Col>
          <Col md className='p-3 border'>
            {loginAlert.show && (
              <Alert variant={loginAlert.variant} onClose={handleCloseLoginAlert} dismissible>
                {loginAlert.message}
              </Alert>
            )}
            <h2 className='h2 fw-bold'>View FHIR Data</h2>
            <div>

              <h3 className="fw-bold mb-1 h4">Patient Access API</h3>
              <div className='border border-2 rounded mb-3 p-2'>
                <Form noValidate onSubmit={memberDataForm.handleSubmit} className="d-flex align-items-center gap-2" role="form" aria-label="FHIR Member View">
                  <Form.Group className="d-flex align-items-center gap-2 me-2">
                    <Form.Label htmlFor="memberId" className="mb-0">View Data by Member ID</Form.Label>
                    <Form.Control
                      id="memberId"
                      name="memberId"
                      type="text"
                      value={memberDataForm.values.memberId}
                      onChange={memberDataForm.handleChange}
                      onBlur={memberDataForm.handleBlur}
                      isInvalid={!!memberDataForm.errors.memberId && memberDataForm.touched.memberId}
                      placeholder="Member ID"
                      aria-invalid={!!memberDataForm.errors.memberId && memberDataForm.touched.memberId}
                      aria-describedby={
                        memberDataForm.errors.memberId && memberDataForm.touched.memberId ? 'memberId-error' : ''
                      }
                      aria-required="true"
                      className="w-auto"
                    />
                    <Form.Control.Feedback id="memberId-error" type="invalid" role="alert">
                      {memberDataForm.errors.memberId}
                    </Form.Control.Feedback>
                  </Form.Group>

                  <Form.Group className="">
                    <Button
                      type="submit"
                      variant='primary'
                      disabled={patientLoading || !memberDataForm.values.memberId}
                    >
                      {patientLoading ? (
                        <FhirLoader message='Loading...' hidden={true} small={true} />
                      ) : (
                        'View Data'
                      )}
                    </Button>
                  </Form.Group>
                </Form>

                {
                  patientStatus === 'error' && (
                    <div className='text-danger mt-3'>
                      Profile not found.
                    </div>
                  )
                }
              </div>
              <h3 className="fw-bold mb-1 h4">Provider Directory API and Formulary API</h3>
              <div className='border border-2 rounded mb-3 p-2'>
                <Form noValidate onSubmit={(e) => handleSearchProviderView(e, 'all')} className="d-flex align-items-center gap-2" role="form" aria-label="View All FHIR Provider Resources">
                  <Form.Group className="d-flex align-items-center gap-2 me-2">
                    <Form.Label htmlFor="searchProviderResource" className="mb-0">View All by Resource Type</Form.Label>
                    <Form.Select
                      id="searchProviderResource"
                      name="viewAllProviderResource"
                      value={viewAllProviderFhirResource}
                      onChange={(e) => setViewAllProviderFhirResource(e.target.value)}
                      className="w-auto"
                    >
                      <option value="" disabled>Select Resource</option>
                      {providerResources.map((resource) => (
                        <option key={resource.label} value={resource.fhirResource}>
                          {resource.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group>
                    <Button type="submit" variant='primary' disabled={!viewAllProviderFhirResource}>
                      View Data
                    </Button>
                  </Form.Group>
                </Form>

                <Form noValidate onSubmit={(e) => handleSearchProviderView(e, 'search')} className="d-flex align-items-center gap-2 mt-3" role="form" aria-label="Search FHIR Provider Resources">
                  <Form.Group className='d-flex align-items-center gap-2 me-2'>
                    <Form.Label htmlFor="viewAllProviderResource" className="mb-0">Search</Form.Label>
                    <Form.Select
                      id='viewAllProviderResource'
                      as='select'
                      name='viewAllProviderResource'
                      value={searchProviderResource.resource}
                      onChange={(e) => setSearchProviderResource((ps) => ({ ...ps, resource: e.target.value }))}
                    >
                      <option value='' disabled> Select Resource</option>
                      {
                        providerResources.filter((providerResource) => Boolean(providerResource.filter)).map((resource) => <option key={resource.label} value={resource.fhirResource}>{resource.fhirResource} {resource.filter}</option>)
                      }
                    </Form.Select>
                    <Form.Label htmlFor="searchTerms" className="visually-hidden">Enter search term</Form.Label>
                    <Form.Control
                      id='searchTerms'
                      type='text'
                      name='searchTerms'
                      placeholder='Name'
                      value={searchProviderResource.searchTerms}
                      onChange={(e) => setSearchProviderResource((ps) => ({ ...ps, searchTerms: e.target.value }))}
                    >
                    </Form.Control>
                  </Form.Group>
                  <Form.Group>
                    <Button type='submit' variant='primary' disabled={!searchProviderResource.resource}>
                      Search
                    </Button>
                  </Form.Group>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  )
}

export default isAuthenticated(Admin)
